import { ServerError, useQuery } from "@apollo/client";
import { useSnackbar } from "notistack";
import { ReactElement, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  GetRegistrationQuery,
  Submit10DlcRegistrationMutation,
  Submit10DlcRegistrationMutationVariables,
  Update10DlcBrandAndCampaignRegistrationMutation,
  Update10DlcBrandAndCampaignRegistrationMutationVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import {
  GET_REGISTRATION,
  SUBMIT_TENDLC_REGISTRATION,
  UPDATE_TENDLC_BRAND_AND_CAMPAIGN_REGISTRATION,
} from "~frontend/graphql/tenDlcRegistrations";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import useMutation from "~frontend/util/useMutation";

import { ExternalRegistration } from "./ExternalRegistration";
import { InternalRegistration } from "./InternalRegistration";

export const Registration = (): ReactElement => {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const intl = useIntl();
  const canUseBuiltInForm = useFeatureFlag("tenDlcRegistrationInApp");
  const { enqueueSnackbar } = useSnackbar();

  const { campaignRegistrationId } = useParams<{
    campaignRegistrationId: string;
  }>();

  const navigate = useNavigate();
  useEffect(() => {
    if (!campaignRegistrationId && canUseBuiltInForm) {
      void navigate(subaccountLink(`/phone-numbers`));
    }
  }, [campaignRegistrationId, canUseBuiltInForm, navigate, subaccountLink]);

  const { data, loading, error } = useQuery<GetRegistrationQuery>(
    GET_REGISTRATION,
    {
      variables: {
        input: {
          subaccountId: subaccount?.id,
          campaignRegistrationId,
        },
      },

      skip: !subaccount?.id || !campaignRegistrationId,
    }
  );

  useEffect(() => {
    if (error && (error?.networkError as ServerError)?.statusCode === 404) {
      enqueueSnackbar(
        intl.formatMessage({
          defaultMessage: "10DLC Registration not found",
          description: "Error message shown when a registration is not found",
        }),
        { variant: "error" }
      );
      void navigate(subaccountLink(`/phone-numbers`));
    }
  }, [enqueueSnackbar, error, intl, navigate, subaccountLink]);

  const [updateRegistration] = useMutation<
    Update10DlcBrandAndCampaignRegistrationMutation,
    Update10DlcBrandAndCampaignRegistrationMutationVariables
  >(UPDATE_TENDLC_BRAND_AND_CAMPAIGN_REGISTRATION, {
    onErrorSnackbarMessage: "Error updating registration",
    refetchQueries: [GET_REGISTRATION],
    awaitRefetchQueries: true,
  });

  const [submitRegistrationForReview] = useMutation<
    Submit10DlcRegistrationMutation,
    Submit10DlcRegistrationMutationVariables
  >(SUBMIT_TENDLC_REGISTRATION, {
    onErrorSnackbarMessage: "Error submitting registration for review",
    refetchQueries: [GET_REGISTRATION],
    awaitRefetchQueries: true,
  });

  const handleSave = async (
    input: Omit<
      Update10DlcBrandAndCampaignRegistrationMutationVariables["input"],
      "subaccountId"
    >
  ): Promise<any> => {
    return await updateRegistration({
      variables: {
        input: {
          ...input,
          subaccountId: subaccount?.id,
        },
      },
    });
  };

  const handleSubmitToStaff = async () => {
    const { errors } = await submitRegistrationForReview({
      variables: {
        input: {
          subaccountId: subaccount?.id,
          campaignRegistrationId: campaignRegistrationId!,
        },
      },
    });
    return { errors: errors as string[] | undefined | null };
  };

  if (canUseBuiltInForm) {
    return (
      <AsyncWrapper loading={loading} errors={error} data={data}>
        {({ getRegistration }) => (
          <InternalRegistration
            registration={getRegistration}
            handleSave={handleSave}
            handleSubmitToStaff={handleSubmitToStaff}
          />
        )}
      </AsyncWrapper>
    );
  } else {
    return <ExternalRegistration />;
  }
};
